// Twee manieren om naar dezelfde lijst te kijken: als bord en als tabel.
//
// Het bord groepeert op een eigenschap — "status" geeft kolommen bezig / wacht / klaar — en je
// versleept een notitie naar een andere kolom om die eigenschap te zetten. Dat is de hele
// truc: geen apart projectbeheer, gewoon je notities met een veld erop.
//
// De tabel is voor als je wilt vergelijken in plaats van verslepen: één regel per notitie, met
// de eigenschappen die je kiest als kolommen.
//
// Allebei werken ze op de lijst die er toch al is: hetzelfde filter, dezelfde volgorde. Een
// weergave is dus geen nieuwe zoekopdracht maar een andere bril.

import { useMemo, useState } from "react";
import type { EigenschapRegel, Rij } from "../api.ts";
import { Icoon } from "./Icoon.tsx";
import { groepeerOpEigenschap, ZONDER } from "./groeperen.ts";

export function Bord({
  rijen,
  sleutel,
  openId,
  kies,
  zetWaarde
}: {
  rijen: Rij[];
  sleutel: string;
  openId?: string;
  kies: (id: string) => void;
  /** Een notitie naar een andere kolom slepen zet die eigenschap. */
  zetWaarde: (id: string, waarde: string) => void;
}) {
  const [boven, setBoven] = useState<string | null>(null);
  const kolommen = useMemo(() => groepeerOpEigenschap(rijen, sleutel), [rijen, sleutel]);

  return (
    <div className="bord">
      {kolommen.map((kolom) => (
        <div
          key={kolom.waarde}
          className={`bordkolom${boven === kolom.waarde ? " sleepdoel" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setBoven(kolom.waarde);
          }}
          onDragLeave={() => setBoven((nu) => (nu === kolom.waarde ? null : nu))}
          onDrop={(e) => {
            e.preventDefault();
            setBoven(null);
            const id = e.dataTransfer.getData("text/notitie");
            // Naar de "zonder"-kolom slepen betekent: dit veld hoort hier niet meer.
            if (id) zetWaarde(id, kolom.waarde === ZONDER ? "" : kolom.waarde);
          }}
        >
          <div className="bordkop">
            <span>{kolom.waarde}</span>
            <span className="telling">{kolom.rijen.length}</span>
          </div>
          {kolom.rijen.map((rij) => (
            <button
              key={rij.id}
              className="bordkaart"
              aria-current={rij.id === openId}
              draggable
              onDragStart={(e) => e.dataTransfer.setData("text/notitie", rij.id)}
              onClick={() => kies(rij.id)}
            >
              <span className="titel">{rij.titel || "Zonder titel"}</span>
              {rij.fragment && <span className="fragment">{rij.fragment}</span>}
              {rij.taken.totaal > 0 && (
                <span className="meta">
                  <Icoon naam="taken" grootte={12} /> {rij.taken.af}/{rij.taken.totaal}
                </span>
              )}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

export function Tabel({
  rijen,
  kolommen,
  openId,
  kies
}: {
  rijen: Rij[];
  kolommen: string[];
  openId?: string;
  kies: (id: string) => void;
}) {
  return (
    <div className="tabelvak">
      <table className="notitietabel">
        <thead>
          <tr>
            <th>Notitie</th>
            {kolommen.map((sleutel) => (
              <th key={sleutel}>{sleutel}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rijen.map((rij) => (
            <tr key={rij.id} aria-current={rij.id === openId} onClick={() => kies(rij.id)}>
              <td>{rij.titel || "Zonder titel"}</td>
              {kolommen.map((sleutel) => (
                <td key={sleutel}>{(rij.eigenschappen ?? {})[sleutel] ?? ""}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Het balkje waarmee je kiest waarop het bord groepeert of welke kolommen de tabel toont. */
export function WeergaveKiezer({
  weergave,
  sleutel,
  bekend,
  kies
}: {
  weergave: "bord" | "tabel";
  sleutel: string;
  bekend: EigenschapRegel[];
  kies: (sleutel: string) => void;
}) {
  if (bekend.length === 0) {
    return (
      <p className="leeg" style={{ padding: "16px 12px" }}>
        Nog geen eigenschappen. Open een notitie en zet er een veld boven — bijvoorbeeld{" "}
        <strong>status: bezig</strong> — dan kun je er hier op groeperen.
      </p>
    );
  }
  return (
    <div className="lijstregel" style={{ padding: "0 4px 6px" }}>
      <span>{weergave === "bord" ? "Groeperen op" : "Kolom"}</span>
      <select value={sleutel} onChange={(e) => kies(e.target.value)} aria-label="Eigenschap kiezen">
        {bekend.map((b) => (
          <option key={b.sleutel} value={b.sleutel}>
            {b.sleutel} ({b.aantal})
          </option>
        ))}
      </select>
    </div>
  );
}
